import { Controller } from '@hotwired/stimulus';

// Companion controller to bd-selection for the BD monthly page.
//
// bd-selection (existing) drives the .is-selected / .is-added classes
// on .bd-card targets. This controller observes those changes, toggles
// the sticky recap-bar, updates the choice label + CTA copy, and — right
// before the form submits — mirrors the DOM-only selection into hidden
// form inputs that Turbo Drive will POST.
//
// Submission itself goes through Turbo Drive (form_with), per the
// rails-hotwire skill rule "Use Turbo Drive for Form Submissions". A
// failed submit (422) is re-rendered server-side; a success returns 303
// to the confirmation page. The previous fetch+document.write hack is
// gone.
export default class extends Controller {
  static targets = [
    'recapBar', 'cta', 'ctaLabel', 'choiceTitle',
    'addressShort', 'selectedInput', 'totalInput', 'extrasInputs',
    'payOverlay'
  ];
  static values = { hasPaymentMethod: Boolean };

  connect() {
    this.observer = new MutationObserver((mutations) => {
      // Only react to class changes on .bd-card targets — ignore the
      // recap-bar / overlay toggles we drive ourselves (avoids a loop
      // where adding `is-visible` to the recap-bar fires the observer,
      // which calls refresh(), which adds the class again).
      const cardChanged = mutations.some(
        (m) => m.target.classList && m.target.classList.contains('bd-card')
      );
      if (cardChanged) this.refresh();
    });
    this.observer.observe(this.element, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
    this.refresh();
  }

  disconnect() {
    if (this.observer) this.observer.disconnect();
  }

  refresh() {
    const selectedCard = this.element.querySelector('.bd-card.is-selected');
    const upsellCards = Array.from(this.element.querySelectorAll('.bd-card.is-added'));

    if (!selectedCard) {
      this.recapBarTarget.hidden = true;
      this.recapBarTarget.classList.remove('is-visible');
      document.body.classList.remove('recap-active');
      return;
    }

    this.recapBarTarget.hidden = false;
    this.recapBarTarget.classList.add('is-visible');
    document.body.classList.add('recap-active');

    if (this.hasChoiceTitleTarget) {
      const mainTitle = selectedCard.dataset.bdTitle || '';
      const extraTitles = upsellCards
        .map((c) => c.dataset.bdTitle)
        .filter(Boolean);

      let label = mainTitle;
      if (extraTitles.length > 0) {
        const suffix =
          extraTitles.length === 1
            ? `+ 1 BD supplémentaire (${extraTitles[0]})`
            : `+ ${extraTitles.length} BD supplémentaires (${extraTitles.join(', ')})`;
        label = `${mainTitle} · ${suffix}`;
      }
      this.choiceTitleTarget.textContent = label;
    }

    const total = upsellCards.reduce(
      (sum, c) => sum + parseFloat(c.dataset.bdPrix || '0'),
      0
    );

    if (this.hasCtaLabelTarget) {
      this.ctaLabelTarget.textContent =
        total > 0
          ? `Valider et payer ${total.toFixed(2).replace('.', ',')} €`
          : 'Valider mon choix';
    }
  }

  // Click handler on the submit button — fires before the form's submit
  // event so we can sync the hidden inputs from the current DOM
  // selection. We do NOT call event.preventDefault — the native submit
  // proceeds and Turbo Drive intercepts it.
  prepareSubmit() {
    const selectedCard = this.element.querySelector('.bd-card.is-selected');
    if (!selectedCard) return;

    const upsellCards = Array.from(this.element.querySelectorAll('.bd-card.is-added'));
    const total = Math.round(
      upsellCards.reduce((s, c) => s + parseFloat(c.dataset.bdPrix || '0') * 100, 0)
    );

    if (this.hasSelectedInputTarget) {
      this.selectedInputTarget.value = selectedCard.dataset.bdTemplateId || '';
    }
    if (this.hasTotalInputTarget) {
      this.totalInputTarget.value = String(total);
    }
    if (this.hasExtrasInputsTarget) {
      this.extrasInputsTarget.replaceChildren();
      upsellCards.forEach((c) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'upsell_bd_ids[]';
        input.value = c.dataset.bdTemplateId || '';
        this.extrasInputsTarget.appendChild(input);
      });
    }

    if (this.hasPayOverlayTarget) {
      this.payOverlayTarget.hidden = false;
    }
  }
}
